import React, { useState, useEffect } from 'react';
import requisitosService from '../services/requisitosService';
import '../styles/UserManagement.css';
import '../styles/theme/toast.css';

const RequisitosSociaManagement = () => {
  const [requisitos, setRequisitos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [nuevoRequisito, setNuevoRequisito] = useState('');
  const [nuevaDescripcion, setNuevaDescripcion] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [editNombre, setEditNombre] = useState('');
  const [editDescripcion, setEditDescripcion] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Cargar requisitos desde la API
  useEffect(() => {
    const loadRequisitos = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await requisitosService.getAllRequisitos();
        setRequisitos(data);
      } catch (error) {
        console.error('Error cargando requisitos:', error);
        setError('Error al cargar los requisitos');
      } finally {
        setLoading(false);
      }
    };

    loadRequisitos();
  }, []);

  // Filtrar requisitos por nombre o descripción
  const filteredRequisitos = requisitos.filter(r =>
    r.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (r.descripcion && r.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const total = filteredRequisitos.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginatedRequisitos = filteredRequisitos.slice((page - 1) * pageSize, page * pageSize);

  const handleAgregar = async (_e) => {
    if (!nuevoRequisito.trim()) return;
    
    try {
      setLoading(true);
      const nuevoRequisitoData = {
        nombre: nuevoRequisito.trim(),
        descripcion: nuevaDescripcion.trim() || ''
      };
      
      await requisitosService.createRequisito(nuevoRequisitoData);
      
      // Recargar la lista completa para asegurar sincronización
      const requisitosActualizados = await requisitosService.getAllRequisitos();
      setRequisitos(requisitosActualizados);
      
      setNuevoRequisito('');
      setNuevaDescripcion('');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (error) {
      console.error('Error creando requisito:', error);
      setError('Error al crear el requisito');
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (id, nombre, descripcion = '') => {
    setEditandoId(id);
    setEditNombre(nombre);
    setEditDescripcion(descripcion);
  };

  const handleGuardar = async (id) => {
    try {
      setLoading(true);
      const datosActualizados = {
        nombre: editNombre,
        descripcion: editDescripcion
      };
      
      await requisitosService.updateRequisito(id, datosActualizados);
      
      // Recargar la lista completa para asegurar sincronización
      const requisitosActualizados = await requisitosService.getAllRequisitos();
      setRequisitos(requisitosActualizados);
      
      setEditandoId(null);
      setEditNombre('');
      setEditDescripcion('');
    } catch (error) {
      console.error('Error actualizando requisito:', error);
      setError('Error al actualizar el requisito');
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async (id) => {
    if (window.confirm('¿Seguro que deseas eliminar este requisito?')) {
      try {
        setLoading(true);
        await requisitosService.deleteRequisito(id);
        
        // Recargar la lista completa para asegurar sincronización
        const requisitosActualizados = await requisitosService.getAllRequisitos();
        setRequisitos(requisitosActualizados);
      } catch (error) {
        console.error('Error eliminando requisito:', error);
        setError('Error al eliminar el requisito');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      {showToast && (
        <div className="custom-toast custom-toast-success">
          ¡Requisito registrado exitosamente!
        </div>
      )}
      <div className="user-management-container">
        <div className="page-header">
          <div className="header-title">
            <h1>Gestión de Requisitos de Socia</h1>
            <p>Administra los requisitos/documentos que la fundación solicita o recibe de las socias</p>
          </div>
        </div>

        <div className="user-management-actions">
          <div className="search-section">
            <div className="search-input">
              <input
                type="text"
                placeholder="Buscar por nombre o descripción..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <button type="button" title="Buscar">
                <i className="fas fa-search"></i>
              </button>
            </div>
          </div>
          <button 
            className="btn-primary register-btn create-btn"
            onClick={() => { 
              setShowModal(true); 
              setNuevoRequisito(''); 
              setNuevaDescripcion(''); 
              setError(''); 
            }}
          >
            <i className="fas fa-plus"></i>
            Agregar Requisito
          </button>
        </div>

        {loading && (
          <div className="loading-message">
            <i className="fas fa-spinner fa-spin"></i> Cargando requisitos...
          </div>
        )}

        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-triangle"></i> {error}
          </div>
        )}

        {/* Modal flotante para agregar requisito */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal-container">
              <button 
                className="modal-close-btn" 
                onClick={() => setShowModal(false)} 
                title="Cerrar"
              >
                <i className="fas fa-times"></i>
              </button>
              
              <form onSubmit={async (e) => { 
                e.preventDefault();
                try {
                  await handleAgregar(e);
                  setShowModal(false);
                } catch (error) {
                  // El error ya se maneja en handleAgregar
                }
              }} className="requisito-form">
                <div className="form-section">
                  <h3>
                    <i className="fas fa-plus-circle"></i>
                    Agregar nuevo requisito
                  </h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="nombre">Nombre del Requisito</label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        placeholder="Ingrese el nombre del requisito..."
                        value={nuevoRequisito}
                        onChange={e => setNuevoRequisito(e.target.value)}
                        autoFocus
                        maxLength={24}
                        required
                      />
                      <small className="form-hint">
                        Máximo 24 caracteres. Restantes: {24 - nuevoRequisito.length}
                      </small>
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="descripcion">Descripción</label>
                      <textarea
                        id="descripcion"
                        name="descripcion"
                        placeholder="Ingrese una descripción breve..."
                        value={nuevaDescripcion}
                        onChange={e => setNuevaDescripcion(e.target.value)}
                        maxLength={12}
                      />
                      <small className="form-hint">
                        Máximo 12 caracteres. Restantes: {12 - nuevaDescripcion.length}
                      </small>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary">
                    <i className="fas fa-plus"></i> Agregar Requisito
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre del Requisito</th>
                <th>Descripción</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRequisitos.map(r => (
                <tr key={r.id}>
                  <td><span className="code-id-field">{r.id}</span></td>
                  <td>
                    {editandoId === r.id ? (
                      <input
                        type="text"
                        className="form-control"
                        value={editNombre}
                        onChange={e => setEditNombre(e.target.value)}
                        style={{ maxWidth: 200 }}
                        maxLength={24}
                        placeholder="Máx 24 caracteres"
                      />
                    ) : (
                      r.nombre
                    )}
                  </td>
                  <td>
                    {editandoId === r.id ? (
                      <textarea
                        className="form-control"
                        value={editDescripcion}
                        onChange={e => setEditDescripcion(e.target.value)}
                        style={{ maxWidth: 300, minHeight: '60px', resize: 'vertical' }}
                        maxLength={12}
                        placeholder="Máx 12 caracteres"
                      />
                    ) : (
                      <span title={r.descripcion}>
                        {r.descripcion || 'Sin descripción'}
                      </span>
                    )}
                  </td>
                  <td className="actions-cell">
                    {editandoId === r.id ? (
                      <>
                        <button className="action-btn edit-btn" onClick={() => handleGuardar(r.id)} title="Guardar">
                          <i className="fas fa-check"></i>
                        </button>
                        <button className="action-btn reset-btn" onClick={() => { setEditandoId(null); setEditNombre(''); setEditDescripcion(''); }} title="Cancelar">
                          <i className="fas fa-times"></i>
                        </button>
                      </>
                    ) : (
                      <>
                        <button className="action-btn edit-btn" onClick={() => handleEditar(r.id, r.nombre, r.descripcion)} title="Editar">
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="action-btn deactivate-btn" onClick={() => handleEliminar(r.id)} title="Eliminar">
                          <i className="fas fa-trash"></i>
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="records-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <div>
            Mostrando <strong>{paginatedRequisitos.length}</strong> de <strong>{total}</strong> requisitos
          </div>
          <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button className="btn-secondary" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Anterior</button>
            <span> Página <strong>{page}</strong> de <strong>{totalPages}</strong> </span>
            <button className="btn-secondary" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Siguiente</button>
            <label style={{ marginLeft: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>Por página:</span>
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
            </label>
          </div>
        </div>
      </div>
    </>
  );
};

export default RequisitosSociaManagement;
